import { MissionBillingPeriod } from '@a_team/models/dist/MissionObject';
import { MissionAdminRole } from '@a_team/models/dist/MissionRole';
import MissionPaymentCycle from '@src/stores/Missions/MissionPaymentCycle';
import { addDays, differenceInDays } from 'date-fns';
import { round } from 'lodash';

const WEEKS_PER_MONTH = 4.33;
const MIN_RATE_MULTIPLIER = 0.75;
const MAX_RATE_MULTIPLIER = 0.85;
const ROUND_TO = 100;
const HOURS_PER_WEEK = 40;

export const getDefaultMonthlyRatesForRole = (
  role: MissionAdminRole,
): Pick<
  MissionAdminRole,
  'builderMonthlyRateMin' | 'builderMonthlyRateMax'
> => {
  if (
    role.builderMonthlyRateMin ||
    role.builderMonthlyRateMax ||
    !role.builderRateMin ||
    !role.builderRateMax
  ) {
    return {};
  }
  return {
    builderMonthlyRateMin: round(
      hourlyRateToMonthlyRate(role.builderRateMin, 'min') || 0,
      ROUND_TO,
    ),
    builderMonthlyRateMax: round(
      hourlyRateToMonthlyRate(role.builderRateMax, 'max') || 0,
      ROUND_TO,
    ),
  };
};

export const hourlyRateToMonthlyRate = (
  hourlyRate: number,
  multiplier?: 'max' | 'min',
): number => {
  const multiplierValue = multiplier
    ? multiplier === 'max'
      ? MAX_RATE_MULTIPLIER
      : MIN_RATE_MULTIPLIER
    : 1;
  return hourlyRate * HOURS_PER_WEEK * WEEKS_PER_MONTH * multiplierValue;
};

export const monthlyRateToHourlyRate = (
  monthlyRate: number,
  multiplier?: 'max' | 'min',
): number => {
  const multiplierValue = multiplier
    ? multiplier === 'max'
      ? MAX_RATE_MULTIPLIER
      : MIN_RATE_MULTIPLIER
    : 1;
  return monthlyRate / (HOURS_PER_WEEK * WEEKS_PER_MONTH * multiplierValue);
};

export const getMissionTitle = (missionTitle: string | undefined) => {
  if (!missionTitle) return '';
  const parts = missionTitle.split(':');
  return parts.length > 1 ? parts.slice(1).join(':').trim() : missionTitle;
};

export const preserveDayInLocalTimezone = (utcDate: Date) => {
  const localDate = new Date(
    utcDate.getUTCFullYear(),
    utcDate.getUTCMonth(),
    utcDate.getUTCDate(),
    0,
    0,
    0,
  );

  return localDate;
};

export function deduceBillingPeriodFromPaymentCycle(
  paymentCycle: MissionPaymentCycle | undefined,
): MissionBillingPeriod | undefined {
  if (!paymentCycle) {
    return undefined;
  }

  // Add one day to the end date to include both the start and end dates in the day count.
  // This is necessary because the differenceInDays function calculates the number of full days,
  // excluding the end date. By adding one day to the end date, we ensure that both the start
  // and end dates are included in the day count.
  const adjustedEnd = addDays(new Date(paymentCycle.data.endDate), 1);
  const daysDiff = differenceInDays(
    adjustedEnd,
    new Date(paymentCycle.data.startDate),
  );

  if (daysDiff >= 28) {
    return MissionBillingPeriod.Monthly;
  } else if (daysDiff >= 13) {
    return MissionBillingPeriod.BiWeekly;
  } else if (daysDiff >= 5) {
    return MissionBillingPeriod.Weekly;
  }

  return undefined;
}
